export const ALT_ENDPOINTS_STORAGE_KEY = 'hc_altEndpoints';
export const READY_PROBE_TIMEOUT_MS = 5_000;
export const READY_PROBE_INTERVAL_MS = 10_000;
export const READY_FAILURES_TO_SHOW_BANNER = 2;

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): KeyValueStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function safeOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

function endpointArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object' && Array.isArray((value as { endpoints?: unknown }).endpoints)) {
    return (value as { endpoints: unknown[] }).endpoints;
  }
  return [];
}

/** Только отдельные HTTPS-origin; относительные URL, userinfo, path, query и fragment не допускаются. */
export function normalizeAltEndpoints(value: unknown, primaryOrigin: string): string[] {
  const primary = safeOrigin(primaryOrigin);
  if (!primary) return [];
  const origins = new Set<string>();
  for (const candidate of endpointArray(value)) {
    if (typeof candidate !== 'string' || !candidate.trim()) continue;
    try {
      const url = new URL(candidate);
      if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) continue;
      if (url.pathname !== '/' || url.search || url.hash || url.origin === primary) continue;
      origins.add(url.origin);
    } catch {
      // Повреждённый адрес из кеша не должен ломать основной интерфейс.
    }
  }
  return [...origins];
}

/** Кешируем только валидные адреса и никогда не ломаем страницу из-за запрета storage. */
export function cacheAltEndpoints(
  response: unknown,
  primaryOrigin: string,
  storage: KeyValueStorage | null = browserStorage(),
): string[] {
  const origin = safeOrigin(primaryOrigin);
  const endpoints = normalizeAltEndpoints(response, primaryOrigin);
  if (!origin || !storage) return endpoints;
  try {
    storage.setItem(ALT_ENDPOINTS_STORAGE_KEY, JSON.stringify({ origin, endpoints }));
  } catch {
    // localStorage может быть запрещён приватным режимом или настройками браузера.
  }
  return endpoints;
}

export function readCachedAltEndpoints(
  primaryOrigin: string,
  storage: KeyValueStorage | null = browserStorage(),
): string[] {
  const origin = safeOrigin(primaryOrigin);
  if (!origin || !storage) return [];
  try {
    const raw = storage.getItem(ALT_ENDPOINTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || (parsed as { origin?: unknown }).origin !== origin) return [];
    return normalizeAltEndpoints((parsed as { endpoints?: unknown }).endpoints, origin);
  } catch {
    return [];
  }
}

export function readinessFailuresAfter(previous: number, status: number | null): number {
  return status === null || status === 0 || status >= 500 ? Math.max(0, previous) + 1 : 0;
}

export function selectAltEndpointForBanner(
  endpoints: unknown,
  primaryOrigin: string,
  consecutiveFailures: number,
): string | null {
  if (consecutiveFailures < READY_FAILURES_TO_SHOW_BANNER) return null;
  return normalizeAltEndpoints(endpoints, primaryOrigin)[0] ?? null;
}
